import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store/useStore';
import useAssistant from '../../store/useAssistant';
import { useDashboardDateRange, useDashboardFilters } from './wallSection';
import { datasetsAPI } from '../../services/api';
import useReportResult from '../../hooks/useReportResult';
import ReportDisplay from './ReportDisplay';
import FreshnessBadge from './FreshnessBadge';
import WidgetPeriodPicker from './WidgetPeriodPicker';
import DrillModal from './DrillModal';
import QueryProgress from './QueryProgress';
import { OTHER_ROW, cellText, exportRows, tableFromRows } from '../../utils/reportTable';
import { downloadCsv, downloadXlsx } from '../../utils/reportExport';
import '../../styles/Reports.css';

const DEFAULT_REFRESH_MINUTES = 30;
const ROW_LIMIT = 1000;

/** What a click on a row, bar or slice can do: filter the dashboard by one of its categories, or show the rows behind it. */
function SelectionMenu({ at, actions, onClose }) {
  useEffect(() => {
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !e.target.closest?.('.report-select-menu')) onClose(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [onClose]);
  const left = Math.min(at.x, window.innerWidth - 300);
  const top = Math.min(at.y, window.innerHeight - 40 - actions.length * 40);
  return createPortal(
    <div className="report-select-menu" role="menu" style={{ left, top }}>
      {actions.map((a) => (
        <button key={a.key} type="button" role="menuitem" onClick={() => { onClose(); a.run(); }} autoFocus={a === actions[0]}>
          {a.label}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/**
 * Dashboard widget for a report. Runs the saved report with the period (the
 * viewer's choice on this widget, the widget's own, the dashboard's, or the
 * report's) and the dashboard's filters. Clicking a category filters the rest
 * of the dashboard by it, or shows the rows behind it.
 *
 * Older widgets keep working: one with a Cube query in its config runs as an
 * unsaved report; one with a single-dataset config queries the dataset.
 */
const ReportWidget = ({ widget }) => {
  const { dataset_name: datasetName, config = {}, report = null } = widget;
  const companyId = useStore((state) => state.currentCompany?.id);
  const wallMode = useStore((state) => state.wallMode);
  const dashboardFilters = useStore((state) => state.dashboardFilters);
  const setDashboardFilter = useStore((state) => state.setDashboardFilter);
  const dashboardDateRange = useDashboardDateRange();
  const filters = useDashboardFilters(widget.id);
  const navigate = useNavigate();

  const legacyQuery = !report && config.cubeQuery ? config.cubeQuery : null;
  const display = report ? { ...(report.display || {}), ...(config.displayOverrides || {}) } : config;
  const cubeQuery = report ? report.cubeQuery : legacyQuery;
  const refreshMinutes = (report ? report.refreshMinutes : config.refreshMinutes) || DEFAULT_REFRESH_MINUTES;
  const title = config.title || report?.name || datasetName || 'Report';

  // Period: the viewer's pick on this widget, else the widget's own, else the
  // dashboard's (for reports that follow it), else the report's own.
  const viewerPeriod = useStore((state) => state.widgetPeriods[widget.id]);
  const followsDashboard = report ? display.inheritDateRange !== false : false;
  const savedPeriod = report ? config.period || null : null;
  const ownRange = (cubeQuery?.timeDimensions || [])[0]?.dateRange || null;
  const hasPeriod = Boolean(report && (cubeQuery?.timeDimensions || []).length);
  const effectiveRange = report ? (viewerPeriod || savedPeriod || (followsDashboard ? dashboardDateRange : null)) : null;
  const defaultPeriod = savedPeriod || (followsDashboard ? dashboardDateRange : null) || ownRange;

  const runnable = useMemo(() => (report || legacyQuery ? (report || { cubeQuery: legacyQuery, display: { displayType: config.displayType } }) : null), [report, legacyQuery, config.displayType]);
  const result = useReportResult({ companyId, report: runnable, dateRange: effectiveRange, filters, limit: ROW_LIMIT, refreshMinutes, enabled: Boolean(runnable) });
  const legacy = useLegacyDataset(!runnable && datasetName ? { companyId, datasetName, config, refreshMinutes } : null);
  const state = runnable ? result : legacy;
  const table = runnable ? result.table : legacy.table;

  const [menu, setMenu] = useState(null);
  const [drill, setDrill] = useState(null);
  const assistantAvailable = useAssistant((s) => Boolean(s.status?.available));
  const openAssistant = useAssistant((s) => s.openPanel);
  const currentDashboardId = useStore((s) => s.currentDashboardId);
  // Asking about this widget starts a conversation that knows the dashboard, the widget and its report.
  const askAbout = (prompt) => openAssistant({
    context: { ...(currentDashboardId ? { dashboardId: Number(currentDashboardId) } : {}), ...(typeof widget.id === 'number' ? { widgetId: widget.id } : {}), ...(report?.id ? { reportId: report.id } : {}) },
    focus: { title, kind: 'report' },
    prompt,
  });

  // The categories a clicked row can filter the dashboard by.
  const onDashboard = !wallMode && Boolean(widget.id) && !String(widget.id).startsWith('preview');
  const filterActions = (row) => (table?.columns || [])
    .filter((c) => c.role === 'dimension' && c.member && !(row[OTHER_ROW] && row.__otherOf === c.key))
    .filter((c) => row[c.key] !== null && row[c.key] !== undefined && row[c.key] !== '')
    .map((c) => {
      const value = String(row[c.key]);
      const active = dashboardFilters[c.member]?.values?.length === 1 && dashboardFilters[c.member].values[0] === value;
      return {
        key: `filter:${c.member}`,
        label: active ? `Clear the dashboard filter on ${c.label}` : `Filter the dashboard: ${c.label} is ${cellText(c, row[c.key])}`,
        run: () => setDashboardFilter(c.member, active ? [] : [value], { label: c.label, sourceWidgetId: widget.id }),
      };
    });

  const onSelect = (row, event, info = {}) => {
    const actions = onDashboard ? filterActions(row) : [];
    if (cubeQuery && !row[OTHER_ROW]) {
      actions.push({ key: 'drill', label: 'Show the rows behind this', run: () => setDrill({ row, annotation: result.result?.annotation || {}, query: result.query || cubeQuery, measure: info.measure || null }) });
    }
    if (assistantAvailable && onDashboard && !row[OTHER_ROW]) {
      const where = (table?.columns || []).filter((c) => (c.role === 'dimension' || c.role === 'time') && row[c.key] !== null && row[c.key] !== undefined)
        .map((c) => `${c.label} is ${cellText(c, row[c.key])}`).join(', ');
      actions.push({ key: 'ask', label: 'Ask the AI assistant about this', run: () => askAbout(`About "${title}"${where ? `, where ${where}` : ''}: `) });
    }
    if (actions.length === 0) return;
    if (actions.length === 1 && !onDashboard) { actions[0].run(); return; }
    const native = event?.nativeEvent || event;
    const x = native?.clientX ?? 200;
    const y = native?.clientY ?? 200;
    setMenu({ at: { x, y }, actions });
  };

  // The category this widget filtered the dashboard by stays highlighted.
  const own = Object.entries(dashboardFilters).filter(([, f]) => f.sourceWidgetId === widget.id);
  const selected = own.length
    ? (row) => own.every(([member, f]) => {
      const column = table?.columns.find((c) => c.member === member && c.role === 'dimension');
      return column && f.values.includes(String(row[column.key]));
    })
    : undefined;

  const skipped = (table?.filters?.skipped || []).map((m) => dashboardFilters[m]?.label || m.split('.').pop());
  const displayConfig = { ...display, compact: wallMode };
  const rowsForExport = () => exportRows(table);

  const body = () => {
    if (state.loading && !table) return <QueryProgress startedAt={state.startedAt} retrying={state.retrying} />;
    if (state.error) return <div className="report-status report-status--error">⚠ {state.error}</div>;
    if (!table) return null;
    return <ReportDisplay table={table} config={displayConfig} onSelect={runnable && !wallMode ? onSelect : undefined} selected={selected} />;
  };

  const badgeAt = state.at;
  const staleAfterMs = table ? 36 * 60 * 60 * 1000 : Math.max(1, refreshMinutes) * 2 * 60 * 1000;

  return (
    <div className="widget report-widget">
      <div className="widget-header">
        <span className="widget-title" title={report?.id ? 'Open the report page' : undefined}
          onClick={report?.id ? () => navigate(`/reports/${report.id}`) : undefined} style={report?.id ? { cursor: 'pointer' } : undefined}>
          {title}
        </span>
        <span className="report-header-tools">
          {!wallMode && table?.rows?.length > 0 && (
            <>
              <button type="button" className="report-tool" title="Download CSV" onClick={() => downloadCsv(rowsForExport(), title)}>CSV</button>
              <button type="button" className="report-tool" title="Download Excel" onClick={() => downloadXlsx(rowsForExport(), title)}>XLSX</button>
            </>
          )}
          <FreshnessBadge kind="report" at={badgeAt} staleAfterMs={staleAfterMs} loading={state.loading} />
        </span>
      </div>
      <div className="widget-body report-body">
        {state.loading && table && <QueryProgress variant="bar" startedAt={state.startedAt} />}
        {body()}
      </div>
      {!wallMode && skipped.length > 0 && (
        <div className="report-filter-note" title="This report has no link to these fields, so the dashboard filter does not change it.">
          Not filtered by {skipped.join(', ')}
        </div>
      )}
      {hasPeriod && <WidgetPeriodPicker widgetId={widget.id} title={title} defaultPeriod={defaultPeriod} />}
      {menu && <SelectionMenu at={menu.at} actions={menu.actions} onClose={() => setMenu(null)} />}
      {drill && (
        <DrillModal companyId={companyId} row={drill.row} annotation={drill.annotation} query={drill.query} measure={drill.measure} title={title} onClose={() => setDrill(null)} />
      )}
    </div>
  );
};

/** The oldest widgets: a single dataset queried with simple options. */
function useLegacyDataset(source) {
  const [state, setState] = useState({ loading: Boolean(source), error: null, table: null, at: null, startedAt: Date.now() });
  const key = source ? JSON.stringify([source.companyId, source.datasetName, source.config]) : null;
  useEffect(() => {
    if (!source) return undefined;
    let live = true;
    const load = async () => {
      setState((prev) => ({ ...prev, loading: true, startedAt: Date.now() }));
      try {
        const options = {};
        for (const k of ['filter', 'groupBy', 'aggregate', 'orderBy', 'limit']) if (source.config[k]) options[k] = source.config[k];
        const res = await datasetsAPI.query(source.datasetName, source.companyId, options);
        if (!res?.success) throw new Error(res?.error || 'Query failed');
        if (live) setState({ loading: false, error: null, table: tableFromRows(res.data.rows || []), at: res.data.lastSyncedAt || new Date().toISOString(), startedAt: null });
      } catch (err) {
        if (live) setState((prev) => ({ ...prev, loading: false, error: err.response?.data?.error || err.message || 'Failed to load' }));
      }
    };
    load();
    const id = setInterval(load, Math.max(1, source.refreshMinutes) * 60 * 1000);
    return () => { live = false; clearInterval(id); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export default ReportWidget;
